import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {
  Button as AntButton,
  DatePicker,
  Input,
  InputNumber,
  Popover,
  Select,
  Table,
  Tag,
} from 'antd';
import {FilterOutlined} from '@ant-design/icons';
import {Button} from '@dabafinance/react-components';
import moment from 'moment';
import {toast} from 'react-toastify';
import {GET_ALL_STARTUPS} from 'graphql/queries/startupCompanies';
import {GET_STOCK_PRICE_ALERT_HISTORY} from 'graphql/queries/stockPriceAlerts';
import styles from './stockPriceAlerts.module.scss';

const DEFAULT_LIMIT = 10;

const DIRECTION_OPTIONS = [
  {value: 'UP', label: 'Up'},
  {value: 'DOWN', label: 'Down'},
];

const OPERATOR_OPTIONS = [
  {value: 'GREATER_THAN', label: 'Greater than (>)'},
  {value: 'LESS_THAN', label: 'Less than (<)'},
  {value: 'EQUAL_TO', label: 'Equal to (=)'},
];

const formatPrice = value =>
  value === null || value === undefined || value === ''
    ? '--'
    : Number(value).toLocaleString(undefined, {maximumFractionDigits: 2});

const isEmptyValue = value =>
  value === null || value === undefined || value === '';

const StockPriceAlerts = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [companyId, setCompanyId] = useState(null);
  const [direction, setDirection] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [operator, setOperator] = useState(null);
  const [movementValue, setMovementValue] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const {data, loading, refetch} = useQuery(GET_STOCK_PRICE_ALERT_HISTORY, {
    variables: {
      page: 1,
      limit: DEFAULT_LIMIT,
      companyId: null,
      priceFromDate: null,
      priceEndDate: null,
      direction: null,
      priceMovementRange: null,
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => toast.error(error.message),
  });

  const {data: companiesData, loading: loadingCompanies} = useQuery(
    GET_ALL_STARTUPS,
    {errorPolicy: 'all'},
  );

  const response = data?.getStockPriceAlertHistory;
  const alerts = response?.data || [];
  const pagination = response?.pagination || {};

  const companyOptions = (companiesData?.getAllCompanies || [])
    .filter(company => company?.id)
    .map(company => ({
      value: company?.id,
      label: company?.ticker
        ? `${company?.name} (${company?.ticker})`
        : company?.name,
    }));

  // Render inner dropdowns inside the popover so clicking them doesn't
  // register as an outside-click that closes the filter panel.
  const popupContainer = triggerNode => triggerNode.parentNode;

  const buildVariables = (page, pageSize) => {
    const hasMovementRange = Boolean(operator) && !isEmptyValue(movementValue);
    return {
      page,
      limit: pageSize,
      companyId: companyId || null,
      direction: direction || null,
      priceFromDate: fromDate
        ? moment(fromDate).startOf('day').toISOString()
        : null,
      priceEndDate: toDate ? moment(toDate).endOf('day').toISOString() : null,
      priceMovementRange: hasMovementRange
        ? {operator, value: Number(movementValue)}
        : null,
    };
  };

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch(buildVariables(page, pageSize));
  };

  const applyFilters = () => {
    // Operator and value only make sense together.
    if (Boolean(operator) !== !isEmptyValue(movementValue)) {
      toast.info(
        'Select both an operator and a value for the price movement filter',
      );
      return;
    }
    setCurrentPage(1);
    refetch(buildVariables(1, limit));
    setFilterOpen(false);
  };

  const activeFilterCount = [
    companyId,
    fromDate,
    toDate,
    direction,
    operator && !isEmptyValue(movementValue) ? 'movement' : null,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setCompanyId(null);
    setDirection(null);
    setFromDate(null);
    setToDate(null);
    setOperator(null);
    setMovementValue(null);
    setCurrentPage(1);
    setLimit(DEFAULT_LIMIT);
    refetch({
      page: 1,
      limit: DEFAULT_LIMIT,
      companyId: null,
      direction: null,
      priceFromDate: null,
      priceEndDate: null,
      priceMovementRange: null,
    });
  };

  const columns = [
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (text, record) => record?.company?.name || '--',
    },

    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: text => text || '--',
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      render: (text, record) => {
        if (!text) return '--';
        const isUp =
          String(text).toUpperCase().includes('UP') ||
          Number(record?.percentageChange) > 0;
        return <Tag color={isUp ? 'green' : 'red'}>{text}</Tag>;
      },
    },
    {
      title: '% Change',
      dataIndex: 'percentageChange',
      key: 'percentageChange',
      render: value => {
        const num = Number(value);
        if (value === null || value === undefined || !Number.isFinite(num))
          return '--';
        return (
          <span
            className={
              num === 0 ? '' : num > 0 ? styles.positive : styles.negative
            }>
            {num > 0 ? '+' : ''}
            {num.toLocaleString(undefined, {maximumFractionDigits: 2})}%
          </span>
        );
      },
    },
    {
      title: 'Opening Price',
      dataIndex: 'openingPrice',
      key: 'openingPrice',
      render: formatPrice,
    },
    {
      title: 'Previous Last Price',
      dataIndex: 'previousLastPrice',
      key: 'previousLastPrice',
      render: formatPrice,
    },
    {
      title: 'Last Price',
      dataIndex: 'lastPrice',
      key: 'lastPrice',
      render: formatPrice,
    },
    {
      title: 'Price Date',
      dataIndex: 'priceDate',
      key: 'priceDate',
      render: value =>
        value ? moment(value).format('DD, MMM, YYYY hh:mm A') : '--',
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? moment(value).format('DD/MM/YYYY') : '--'),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Stock Price Alerts</h1>
        <Popover
          trigger="click"
          placement="bottomRight"
          visible={filterOpen}
          onVisibleChange={setFilterOpen}
          overlayClassName={styles.filterPopover}
          content={
            <div className={styles.filterPanel}>
              <div className={styles.field}>
                <span className={styles.label}>Company</span>
                <Select
                  showSearch
                  allowClear
                  value={companyId}
                  loading={loadingCompanies}
                  placeholder="All companies"
                  optionFilterProp="label"
                  options={companyOptions}
                  getPopupContainer={popupContainer}
                  onChange={value => setCompanyId(value || null)}
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>From Date</span>
                <DatePicker
                  value={fromDate}
                  placeholder="Select date"
                  getPopupContainer={popupContainer}
                  onChange={value => setFromDate(value || null)}
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>To Date</span>
                <DatePicker
                  value={toDate}
                  placeholder="Select date"
                  getPopupContainer={popupContainer}
                  onChange={value => setToDate(value || null)}
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Direction</span>
                <Select
                  allowClear
                  value={direction}
                  placeholder="Any"
                  options={DIRECTION_OPTIONS}
                  getPopupContainer={popupContainer}
                  onChange={value => setDirection(value || null)}
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>% Change</span>
                <Input.Group compact className={styles.movementGroup}>
                  <Select
                    allowClear
                    value={operator}
                    placeholder="Operator"
                    className={styles.operatorSelect}
                    options={OPERATOR_OPTIONS}
                    getPopupContainer={popupContainer}
                    onChange={value => setOperator(value || null)}
                  />
                  <InputNumber
                    min={0}
                    step={0.01}
                    value={movementValue}
                    placeholder="Value"
                    className={styles.valueInput}
                    onChange={setMovementValue}
                  />
                </Input.Group>
              </div>
              <div className={styles.panelActions}>
                <Button label="Reset" type="secondary" onClick={resetFilters} />
                <Button label="Apply Filters" onClick={applyFilters} />
              </div>
            </div>
          }>
          <AntButton
            size="large"
            icon={<FilterOutlined />}
            className={styles.filterTrigger}>
            Filters
            {activeFilterCount > 0 && (
              <span className={styles.filterCount}>{activeFilterCount}</span>
            )}
          </AntButton>
        </Popover>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={alerts}
        className={styles.table}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: pagination?.total || 0,
          onChange: handlePageChange,
          showSizeChanger: true,
        }}
      />
    </div>
  );
};

export default StockPriceAlerts;
